/**
 * The gardener's Grow account: making it, reading it by their Firebase
 * identity, changing the profile and its photo, a password reset, and
 * deleting the account.
 *
 * Firebase proves who somebody is; this is the account that holds their
 * garden. Deleting it is guarded twice (a summary first, a typed word sent
 * along) because there is no undo.
 *
 * Endpoints:
 * - POST   /users NewAccountDetails → { user } (email from the token; safe
 *   to repeat).
 * - GET    /users/firebase/:firebaseUid → { user }.
 * - PUT    /users/:userId Partial<MySQLUser> → { user } (a postcode the
 *   backend cannot use is refused with POSTCODE_*: api/postcodes).
 * - POST   /users/:userId/photo (multipart: 'photo') → { success, user };
 *   DELETE /users/:userId/photo.
 * - GET    /users/:userId/deletion-summary → { summary }.
 * - DELETE /users/:userId { confirm: 'DELETE' }.
 * - POST   /users/password-reset { email } → { useFallback? } (no sign-in).
 *
 * Who uses it: shared/context/AuthContext (reading the account behind the
 * Firebase identity), sign-up (LoginPage, CompleteSignup), Today's postcode
 * box, and the profile (ProfileDetailsForm, ProfilePhotoCard,
 * ProfileLinksCard, PublicPageCard, DeleteAccountDialog). Not a React
 * Query: AuthContext keeps the account (mysqlUser) and is told of changes
 * (updateMysqlUser, refreshMysqlUser). Auth: the signed-in token, always the person's own
 * account (X-Grow-Garden does not apply).
 *
 * @module api/users
 */

import { apiClient } from './apiClient';
import type { MySQLUser } from '../types/user';
import type { Language } from '../types/i18n';
import { SIGNUP_NAME_SESSION_KEY } from '@/config/tunables/shared';
import { trackEvent } from '@/shared/lib/analytics';
import { growLimit } from '@/shared/lib/growSettings';
import type { SignupFrom } from '@/shared/lib/arrivalSource';

/**
 * Saves changes to the gardener's profile.
 *
 * @param userId - The Grow user.
 * @param userData - The fields to change.
 * @returns The account as saved.
 */
export const updateUserProfile = async (userId: number, userData: Partial<MySQLUser>): Promise<MySQLUser> => {
  const response = await apiClient.put(`/users/${userId}`, userData);
  return response.data.user;
};

/**
 * Puts up a new profile photo.
 *
 * @param userId - The Grow user.
 * @param formData - The photo, under 'photo'.
 * @returns The account with its new photo.
 */
export const uploadProfilePhoto = async (userId: number, formData: FormData): Promise<{ success: boolean; user: MySQLUser }> => {
  const response = await apiClient.post(`/users/${userId}/photo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Takes the profile photo down.
 *
 * @param userId - The Grow user.
 */
export const deleteProfilePhoto = async (userId: number): Promise<void> => {
  await apiClient.delete(`/users/${userId}/photo`);
};

/**
 * The Grow account behind a Firebase identity.
 *
 * @param firebaseUid - The Firebase user's uid.
 * @returns The account.
 */
export const getUserByFirebaseId = async (firebaseUid: string): Promise<MySQLUser> => {
  const response = await apiClient.get(`/users/firebase/${firebaseUid}`);
  return response.data.user;
};

/** What an account holds, so a confirmation can name it. */
export interface AccountDeletionSummary {
  /** How many plants. */
  plants: number;
  /** How many photos. */
  photos: number;
  /** How many notes. */
  notes: number;
  /** How many beds. */
  locations: number;
  /** How many timeline entries. */
  timelines: number;
}

/**
 * Counts what deleting this account would destroy.
 *
 * Read before the confirmation is shown, so the dialog can say "eleven
 * photographs" rather than "your data".
 *
 * @param userId - The Grow user.
 */
export const getAccountDeletionSummary = async (
  userId: number,
): Promise<AccountDeletionSummary> => {
  const response = await apiClient.get(`/users/${userId}/deletion-summary`);
  return response.data.summary;
};

/**
 * Deletes the account and everything in it. There is no undo.
 *
 * The literal word is sent as well as typed: the endpoint refuses a DELETE
 * that does not carry it, so no stray request can empty an account.
 *
 * @param userId - The Grow user.
 */
export const deleteAccount = async (userId: number): Promise<void> => {
  await apiClient.delete(`/users/${userId}`, { data: { confirm: growLimit('DELETE_CONFIRM_WORD') } });
};

/**
 * Where the name typed on the sign-up form waits (sessionStorage) for the
 * Grow account to be made with it, so it is not asked twice.
 */
export const SIGNUP_NAME_KEY = SIGNUP_NAME_SESSION_KEY;

/**
 * What signing up asks for, beyond the identity the token already proves: the
 * name. The postcode is asked on Today after (TodayPostcode), so these are
 * left out then.
 */
export interface NewAccountDetails {
  /** The name the account is made with (the sign-up form's, or the sign-in's own). */
  display_name: string;
  /** A ZIP code given at sign-up; the app leaves it out (Today asks for it). */
  postal_code?: string | null;
  /** Its country, kept only beside a ZIP the backend accepts. */
  country?: string | null;
  /** Its latitude, kept only beside a ZIP the backend accepts. */
  latitude?: number | null;
  /** Its longitude, the same. */
  longitude?: number | null;
  /** The share link that brought them here, if one did. */
  referred_share_code?: string | null;
  /** When this phone opened that share's page (ISO); the server credits the share only within welcome.REFERRAL_DAYS (A73). */
  referred_share_opened_at?: string | null;
  /** The founding code a ?founding= link brought, if one did (arrivalSource). */
  founding_code?: string | null;
  /**
   * The language the sign-in page was in (the browser's, or chosen there;
   * startLanguage): the account's preferences start in it, and so does the
   * welcome email.
   */
  language?: Language;
  /** The first tagged link they arrived by (arrivalSource): its source. */
  utm_source?: string;
  /** Its medium. */
  utm_medium?: string;
  /** Its campaign: "founding" for the founding invite. */
  utm_campaign?: string;
  /**
   * Which of the owner's own links they arrived by (arrivalSource): her
   * Instagram ('ig') or Facebook ('fb'). Kept on the account, which then has
   * the follower trial (the backend's trial.FOLLOWER_DAYS).
   */
  from?: SignupFrom | null;
}

/**
 * Creates the database half of an account.
 *
 * Signing up with Firebase proves who someone is; it does not give them a
 * garden. This makes the user, their preferences and their first bed.
 *
 * Email is not sent: the server takes it from the verified token, so nobody
 * can sign up as themselves and claim another address. Calling it twice is
 * safe — the second call returns the account the first one made.
 *
 * @param details - The name, and whatever else was asked at sign-up.
 * @returns The account, with the server's `created` flag: true when this call made it, false when it was already there.
 */
export const createAccount = async (details: NewAccountDetails): Promise<MySQLUser & { created?: boolean }> => {
  const response = await apiClient.post('/users', details);
  // Counted once per browser session: the call is safe to repeat and a repeat is not a new gardener.
  try {
    if (window.sessionStorage.getItem('grow.signUpCounted') !== '1') {
      window.sessionStorage.setItem('grow.signUpCounted', '1');
      trackEvent('sign_up');
    }
  } catch { trackEvent('sign_up'); }
  return { ...response.data.user, created: response.data.created };
};

/**
 * Asks for a password reset link to be emailed.
 *
 * The answer is deliberately the same whether or not that address has an
 * account — a reset form that says "no such account" is a way to find out who
 * has one — so there is nothing useful to return, and callers should say
 * something equally vague.
 *
 * @param email - The address to send the link to.
 * @returns useFallback: true when the server could not send it and Firebase's own email should be used.
 */
export const requestPasswordReset = async (email: string): Promise<{ useFallback: boolean }> => {
  const response = await apiClient.post('/users/password-reset', { email });
  return { useFallback: Boolean(response.data?.useFallback) };
};
